
import { useState, ChangeEvent, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { useProgrammaticLoginAuthenticationProgrammaticLoginPost } from '../../../api/generated/fastAPI';
import { useCurrentUser } from '../../../providers/CurrentUserProvider'; // adjust path
import { useLocation, useNavigate } from 'react-router-dom';
import InputField from '../../../components/Form/InputField';
import useMessage from '../../../hooks/useMessage';
import SnackBar from '../../../components/Alerts/SnackBar';
import useColorPalette from '../../../hooks/useColorPalette';

const isDemo = process.env.REACT_APP_IS_DEMO === 'true';
const demoCreds = {
  username: process.env.REACT_APP_DEMO_USER || '',
  password: process.env.REACT_APP_DEMO_PASSWORD || '',
};
const isAC = String(process.env.REACT_APP_APPLICATION).toLowerCase() === 'assay-central';

export default function LoginCard() {
    const [credentials, setCredentials] = useState<{ username: string; password: string }>(() =>
    isDemo ? demoCreds : { username: '', password: '' }
    );
    const [ errorCredentials, setErrorCredentials ] = useState<{username: boolean, password: boolean}>({username: false, password: false});
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isDisabled, setIsDisabled ] = useState(false);
    

    const loginMutation = useProgrammaticLoginAuthenticationProgrammaticLoginPost();
    const { refetch: refetchUser } = useCurrentUser();
    const navigate = useNavigate();
    const location = useLocation();

    const getRedirectTarget = () => {
        const params = new URLSearchParams(location.search);
        const next = params.get("next");
        return next || sessionStorage.getItem("redirectAfterLogin") || (isAC ? "/files" : "/models");
    };

    const { palette } = useColorPalette(); 


     const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();

    const handleProgrammaticLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        setIsLoading(true);
        setIsDisabled(true);

        try {
            await loginMutation.mutateAsync({ data: credentials });
            // make sure the context has the fresh user/roles before routing
            try {
                await refetchUser();
            } catch { /* ignore if it 204s or similar; navigation will still proceed */ }

            const target = getRedirectTarget();
            sessionStorage.removeItem("redirectAfterLogin");

            // replace to avoid bouncing back to /login on back button
            navigate(target, { replace: true });
        } catch (err: any) {
            console.log(err);
            setPopupMessage('There was an error logging you in. Please try again.', false);
        }

        setIsLoading(false);
        setIsDisabled(false);
        
    }
  

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setCredentials({
            ...credentials,
            [e.target.name]: e.target.value,
        })
        setErrorCredentials({
            ...errorCredentials,
            [e.target.name]: false,
        })
    }

    useEffect(() => {
        if (!credentials.username || !credentials.password) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [credentials]);



    return (
        <form id='login-form' onSubmit={handleProgrammaticLogin} className='p-10 bg-white max-w-[500px] w-full border border-primary rounded-xl overflow-hidden flex flex-col gap-10'>
            <div className='flex gap-3 justify-start items-center'>
                <img src={`${process.env.PUBLIC_URL}/${palette.logo}`} className='w-[52px]' alt={`${palette.application}-logo`} />
                <p className='text-[1.5rem] text-primary font-semibold'>{process.env.REACT_APP_META_TITLE || 'Assay Central'}</p>
            </div>
            <p className='text-default text-[36px] font-semibold leading-10'>Sign In</p>
            <div className='w-full flex flex-col justify-center items-center gap-8'>
                <InputField 
                    handleChange={handleInput} 
                    isRequired 
                    label='Username' 
                    name='username' 
                    id='username' 
                    value={credentials.username} 
                    type='text'
                />
                <InputField 
                    handleChange={handleInput} 
                    isRequired 
                    label='Password' 
                    name='password' 
                    id='password' 
                    value={credentials.password} 
                    type='password'
                />
               
            </div>

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>

            {/* {errorMessage && (
                <ErrorMessage errorMessage={errorMessage} />
            )} */}
            <button title='Log In' type='submit' disabled={isDisabled} className='transition-all flex justify-center items-center gap-3 disabled:border-quaternary/[0.3] disabled:bg-quaternary/[0.3] w-full p-3 text-white bg-primary bg-opacity-100 rounded-lg font-medium hover:bg-darken'>
            {isLoading ? 
            <>
            <CircularProgress size={16} color='inherit'/>
            <p>Logging in</p>
            </>
            : 
            <p>Login</p>
            }
            </button>
        </form>
    )
}